import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsApi } from "@/api";

export default function Settings() {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsApi.list(),
  });

  useEffect(() => {
    if (data?.data) {
      const map: Record<string, string> = {};
      data.data.forEach((s) => { map[s.key] = s.value; });
      setValues(map);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (settings: Array<{ key: string; value: string }>) =>
      settingsApi.update(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleSave = () => {
    const settings = Object.entries(values).map(([key, value]) => ({ key, value }));
    mutation.mutate(settings);
  };

  if (isLoading) return <p className="text-slate-500">読み込み中...</p>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">設定</h2>

      <div className="bg-white p-6 rounded-lg shadow-sm border max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium">SMS送信時間帯制限</h3>
            <p className="text-xs text-slate-400 mt-1">
              ONの場合、時間帯外のSMSは翌営業時間開始まで自動的に遅延されます
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={values.sms_time_restriction !== "0"}
            onClick={() => setValues({ ...values, sms_time_restriction: values.sms_time_restriction === "0" ? "1" : "0" })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${values.sms_time_restriction === "0" ? "bg-slate-300" : "bg-blue-500"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${values.sms_time_restriction === "0" ? "translate-x-1" : "translate-x-6"}`} />
          </button>
        </div>
        <div className={`flex gap-4 items-center ${values.sms_time_restriction === "0" ? "opacity-40 pointer-events-none" : ""}`}>
          <div>
            <label className="block text-xs text-slate-500 mb-1">開始 (JST)</label>
            <select
              value={values.sms_send_start || "9"}
              onChange={(e) => setValues({ ...values, sms_send_start: e.target.value })}
              className="px-3 py-2 border rounded-md text-sm w-24"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <span className="text-slate-400 mt-5">〜</span>
          <div>
            <label className="block text-xs text-slate-500 mb-1">終了 (JST)</label>
            <select
              value={values.sms_send_end || "20"}
              onChange={(e) => setValues({ ...values, sms_send_end: e.target.value })}
              className="px-3 py-2 border rounded-md text-sm w-24"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="px-6 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
        >
          {mutation.isPending ? "保存中..." : "保存"}
        </button>
        {saved && <span className="text-sm text-green-600">保存しました</span>}
        {mutation.isError && <span className="text-sm text-red-600">保存に失敗しました</span>}
      </div>
    </div>
  );
}
